import { createIcons, Search, Download, MessageSquareText, Pencil, ChevronRight, ChevronLeft, PersonStanding, ChevronsDownUp, ChevronsLeft, X } from 'lucide';
import { findAccessiYesButton } from './accessibility-widget.js';

export default function initTouringStandards() {
  const section = document.querySelector('.touring-standards, .touring-standard-single');
  if (!section) return;

  const pager = section.querySelector('.touring-standards__pager');
  const footer = document.querySelector('.pre-footer-content, .site-footer');
  if (pager && footer) {
    let pagerFrame = null;
    const updatePagerPosition = () => {
      pagerFrame = null;
      // Keep the normal viewport gap until the footer reaches the controls.
      const overlap = Math.max(0, window.innerHeight - footer.getBoundingClientRect().top);
      pager.style.setProperty('--touring-footer-offset', `${overlap}px`);
    };
    const schedulePagerPosition = () => {
      if (pagerFrame === null) {
        pagerFrame = window.requestAnimationFrame(updatePagerPosition);
      }
    };

    window.addEventListener('scroll', schedulePagerPosition, { passive: true });
    window.addEventListener('resize', schedulePagerPosition);
    window.addEventListener('pageshow', schedulePagerPosition);
    if (typeof ResizeObserver !== 'undefined') {
      const pagerObserver = new ResizeObserver(schedulePagerPosition);
      pagerObserver.observe(document.body);
      pagerObserver.observe(footer);
    }
    updatePagerPosition();
  }

  section.querySelectorAll('a[href]').forEach(link => {
    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch (error) {
      return;
    }
    if (!['http:', 'https:'].includes(url.protocol)) return;
    if (url.origin === window.location.origin && !/\.pdf$/i.test(url.pathname)) return;

    link.target = '_blank';
    link.relList.remove('opener');
    link.relList.add('noopener', 'noreferrer');
  });

  // Keep each CMS resource heading and its description together as one row.
  if (section.matches('.touring-standard-single--useful-resources, .touring-standard-single--ready-to-use-support')) {
    const body = section.querySelector('.touring-standard-single__body');
    body?.querySelectorAll(':scope > h2').forEach(heading => {
      const link = heading.querySelector('a[href]');
      if (!link) return;

      link.target = '_blank';
      link.relList.remove('opener');
      link.relList.add('noopener', 'noreferrer');

      const row = document.createElement('div');
      row.className = 'touring-resource-row';
      heading.before(row);
      let next = heading.nextElementSibling;
      row.append(heading);
      while (next && !next.matches('h1, h2, h3, h4, h5, h6')) {
        const following = next.nextElementSibling;
        row.append(next);
        next = following;
      }

      const arrow = document.createElement('span');
      arrow.className = 'touring-resource-row__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      link.append(arrow);
    });
  }

  const homeLink = document.querySelector('.site-header--touring-standards .site-branding a');
  if (homeLink) {
    const label = document.createElement('span');
    label.className = 'touring-standards__home-label';
    label.setAttribute('aria-hidden', 'true');
    label.append('Go to NETS');
    const arrow = document.createElement('i');
    arrow.setAttribute('data-lucide', 'chevrons-left');
    label.append(arrow);
    homeLink.append(label);
    homeLink.addEventListener('keydown', event => {
      if (event.key === 'Escape') homeLink.classList.add('is-label-dismissed');
    });
    homeLink.addEventListener('mouseenter', () => homeLink.classList.remove('is-label-dismissed'));
    homeLink.addEventListener('focus', () => homeLink.classList.remove('is-label-dismissed'));
  }

  createIcons({ icons: { Search, Download, MessageSquareText, Pencil, ChevronRight, ChevronLeft, PersonStanding, ChevronsDownUp, ChevronsLeft, X } });
  const searchDialog = section.querySelector('[data-touring-search-dialog]');
  const searchField = section.querySelector('[data-touring-search-field]');
  const searchSuggestions = section.querySelector('[data-touring-search-suggestions]');
  const feedbackDialog = section.querySelector('[data-touring-feedback-dialog]');
  const feedbackForm = section.querySelector('[data-touring-feedback-form]');
  const feedbackMessage = section.querySelector('[data-touring-feedback-message]');
  const alignFeedbackDialog = () => {
    const button = section.querySelector('[data-touring-feedback]');
    if (!feedbackDialog || !button) return;
    feedbackDialog.style.setProperty('--touring-feedback-top', `${button.getBoundingClientRect().top}px`);
  };
  window.addEventListener('resize', alignFeedbackDialog);
  const alignSearchDialog = () => {
    const button = section.querySelector('[data-touring-search]');
    if (!searchDialog || !button) return;
    searchDialog.style.setProperty('--touring-search-top', `${button.getBoundingClientRect().top}px`);
  };
  window.addEventListener('resize', alignSearchDialog);
  const searchIndex = Array.isArray(window.netsTouringStandards?.searchIndex) ? window.netsTouringStandards.searchIndex : [];
  let activeSuggestionIndex = -1;
  let currentSuggestions = [];

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const isStandardsUrl = url => {
    try {
      const parsedUrl = new URL(url, window.location.href);
      return parsedUrl.origin === window.location.origin && parsedUrl.pathname.replace(/\/+$/, '').startsWith('/touring-standards');
    } catch (error) {
      return false;
    }
  };

  const navigateWithTransition = url => {
    if (!url || prefersReducedMotion) {
      window.location.href = url;
      return;
    }

    section.classList.add('is-transitioning');
    window.setTimeout(() => {
      window.location.href = url;
    }, 180);
  };

  const closeFeedbackDialog = () => {
    if (!feedbackDialog) return;
    feedbackDialog.close();
  };

  [searchDialog, feedbackDialog].forEach(modal => {
    if (!modal) return;
    modal.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...modal.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]')]
        .filter(control => !control.disabled && control.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    });
    modal.addEventListener('close', () => {
      document.body.classList.remove('touring-standards-feedback-open');
      hideSearchSuggestions();
      const trigger = modal === searchDialog ? '[data-touring-search]' : '[data-touring-feedback]';
      section.querySelector(trigger)?.focus({ preventScroll: true });
    });
  });

  const openAccessiYesWidget = fallback => {
    const button = findAccessiYesButton();

    if (button) {
      button.click();
      return;
    }

    if (typeof fallback === 'function') {
      fallback();
    }
  };

  const hideSearchSuggestions = () => {
    if (!searchField || !searchSuggestions) return;

    searchSuggestions.hidden = true;
    searchSuggestions.innerHTML = '';
    searchField.setAttribute('aria-expanded', 'false');
    searchField.removeAttribute('aria-activedescendant');
    activeSuggestionIndex = -1;
    currentSuggestions = [];
  };

  const setActiveSuggestion = index => {
    if (!searchField || !searchSuggestions || !currentSuggestions.length) return;

    activeSuggestionIndex = (index + currentSuggestions.length) % currentSuggestions.length;
    searchSuggestions.querySelectorAll('[data-touring-search-suggestion]').forEach((button, buttonIndex) => {
      const isActive = buttonIndex === activeSuggestionIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      if (isActive) {
        searchField.setAttribute('aria-activedescendant', button.id);
      }
    });
  };

  const submitSearchSuggestion = term => {
    if (!searchField) return;

    searchField.value = term;
    hideSearchSuggestions();

    if (typeof searchField.form?.requestSubmit === 'function') {
      searchField.form.requestSubmit();
      return;
    }

    searchField.form?.submit();
  };

  const renderSearchSuggestions = () => {
    if (!searchField || !searchSuggestions || !searchIndex.length) return;

    const query = searchField.value.trim().toLowerCase();
    if (query.length < 2) {
      hideSearchSuggestions();
      return;
    }

    const startsWithMatches = searchIndex.filter(term => term.toLowerCase().startsWith(query));
    const containsMatches = searchIndex.filter(term => !term.toLowerCase().startsWith(query) && term.toLowerCase().includes(query));
    currentSuggestions = [...startsWithMatches, ...containsMatches].slice(0, 8);

    if (!currentSuggestions.length) {
      hideSearchSuggestions();
      return;
    }

    searchSuggestions.innerHTML = '';
    currentSuggestions.forEach((term, index) => {
      const suggestionLink = document.createElement('a');
      const suggestionUrl = new URL(searchField.form?.action || '/', window.location.origin);
      suggestionUrl.searchParams.set('s', term);
      suggestionLink.id = `touring-standards-search-suggestion-${index}`;
      suggestionLink.href = suggestionUrl.toString();
      suggestionLink.setAttribute('role', 'option');
      suggestionLink.setAttribute('aria-selected', 'false');
      suggestionLink.setAttribute('data-touring-search-suggestion', String(index));
      suggestionLink.textContent = term;
      searchSuggestions.appendChild(suggestionLink);
    });
    searchSuggestions.hidden = false;
    searchField.setAttribute('aria-expanded', 'true');
    activeSuggestionIndex = -1;
  };

  searchField?.addEventListener('input', renderSearchSuggestions);
  searchField?.addEventListener('keydown', event => {
    if (!currentSuggestions.length || !searchSuggestions || searchSuggestions.hidden) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion(activeSuggestionIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion(activeSuggestionIndex - 1);
    } else if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
      event.preventDefault();
      submitSearchSuggestion(currentSuggestions[activeSuggestionIndex]);
    } else if (event.key === 'Escape') {
      hideSearchSuggestions();
    }
  });
  section.addEventListener('click', event => {
    if (!event.target.closest('[data-touring-search]')) {
      return;
    }

    if (!searchDialog) return;

    alignSearchDialog();

    if (feedbackDialog) {
      closeFeedbackDialog();
    }

    if (typeof searchDialog.showModal === 'function') {
      event.preventDefault();
      searchDialog.showModal();
    } else {
      searchDialog.setAttribute('open', '');
      searchDialog.classList.add('is-open');
    }

    window.setTimeout(() => searchField?.focus(), 60);
  });
  section.addEventListener('click', event => {
    if (!event.target.closest('[data-touring-search-close]') || !searchDialog) {
      return;
    }

    if (typeof searchDialog.close === 'function') {
      event.preventDefault();
      searchDialog.close();
      hideSearchSuggestions();
      return;
    }

    searchDialog.removeAttribute('open');
    searchDialog.classList.remove('is-open');
    hideSearchSuggestions();
  });
  section.addEventListener('click', event => {
    if (!event.target.closest('[data-touring-feedback]')) {
      return;
    }

    if (!feedbackDialog) return;

    event.preventDefault();
    if (typeof searchDialog?.close === 'function') {
      searchDialog.close();
    } else {
      searchDialog?.removeAttribute('open');
      searchDialog?.classList.remove('is-open');
    }
    hideSearchSuggestions();
    alignFeedbackDialog();
    feedbackDialog.showModal();
    document.body.classList.add('touring-standards-feedback-open');
    feedbackDialog.querySelector('[data-touring-feedback-page-url]')?.setAttribute('value', window.location.href);
    feedbackDialog.querySelector('[data-touring-feedback-close]')?.focus({ preventScroll: true });
  });
  section.addEventListener('click', event => {
    if (!event.target.closest('[data-touring-feedback-close]') || !feedbackDialog) {
      return;
    }

    event.preventDefault();
    closeFeedbackDialog();
  });
  const getFeedbackRecaptchaToken = () => {
    const recaptcha = window.netsTouringStandards || {};
    if (!recaptcha.recaptchaEnabled) {
      return Promise.resolve(null);
    }

    if (!recaptcha.recaptchaSiteKey || !window.grecaptcha?.ready || !window.grecaptcha?.execute) {
      return Promise.reject(new Error(recaptcha.recaptchaFailLabel || 'The reCAPTCHA check could not be completed. Please try again.'));
    }

    return new Promise(resolve => window.grecaptcha.ready(resolve))
      .then(() => window.grecaptcha.execute(recaptcha.recaptchaSiteKey, { action: recaptcha.recaptchaAction || 'touring_standards_feedback' }));
  };

  feedbackForm?.addEventListener('submit', event => {
    event.preventDefault();

    const ajaxUrl = window.netsTouringStandards?.ajaxUrl || '/wp-admin/admin-ajax.php';
    const submitButton = feedbackForm.querySelector('button[type="submit"]');
    const formData = new FormData(feedbackForm);

    if (feedbackMessage) {
      feedbackMessage.textContent = '';
      feedbackMessage.classList.remove('is-error', 'is-success');
    }

    submitButton?.setAttribute('disabled', 'disabled');

    getFeedbackRecaptchaToken()
      .then(token => {
        if (token) {
          formData.set('recaptcha_token', token);
          formData.set('recaptcha_action', window.netsTouringStandards?.recaptchaAction || 'touring_standards_feedback');
        }

        return fetch(ajaxUrl, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin'
        });
      })
      .then(response => response.json().catch(() => ({})).then(data => ({ response, data })))
      .then(({ response, data }) => {
        const message = data?.data?.message || (response.ok ? 'Thank you. Your feedback has been sent.' : 'The feedback could not be sent. Please try again later.');
        if (!response.ok || !data?.success) {
          throw new Error(message);
        }

        if (feedbackMessage) {
          feedbackMessage.textContent = message;
          feedbackMessage.classList.add('is-success');
        }
        feedbackForm.reset();
      })
      .catch(error => {
        if (feedbackMessage) {
          feedbackMessage.textContent = error.message || 'The feedback could not be sent. Please try again later.';
          feedbackMessage.classList.add('is-error');
        }
      })
      .finally(() => {
        submitButton?.removeAttribute('disabled');
      });
  });
  section.querySelector('[data-touring-print]')?.addEventListener('click', () => window.print());
  section.querySelector('[data-touring-collapse-all]')?.addEventListener('click', () => {
    section.querySelectorAll('.touring-standards__side-nav details').forEach(item => { item.open = false; });
  });
  section.querySelectorAll('.touring-standards__side-nav details > summary').forEach(summary => {
    const details = summary.parentElement;
    let wasOpen = details.open;
    let arrowRotation = wasOpen ? 225 : 405;
    summary.style.setProperty('--touring-nav-arrow-rotation', `${arrowRotation}deg`);
    details.addEventListener('toggle', () => {
      if (details.open === wasOpen) return;
      wasOpen = details.open;
      // Accumulate turns so opening and closing both rotate clockwise.
      arrowRotation += 180;
      summary.style.setProperty('--touring-nav-arrow-rotation', `${arrowRotation}deg`);
    });

    summary.addEventListener('click', event => {
      if (!details.classList.contains('touring-standards__nav-details--empty-parent')) return;
      if (!event.target.closest('a')) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      details.open = !details.open;
    });
  });
  section.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;
    if (link.closest('[data-touring-search], [data-touring-feedback], [data-touring-search-close], [data-touring-feedback-close]')) return;

    const href = link.getAttribute('href') || '';
    if (!href || href.startsWith('#')) return;

    const targetUrl = new URL(link.href, window.location.href);
    const currentUrl = new URL(window.location.href);
    if (targetUrl.origin === currentUrl.origin && targetUrl.pathname === currentUrl.pathname && targetUrl.search === currentUrl.search && targetUrl.hash) {
      return;
    }

    if (!isStandardsUrl(link.href)) return;

    event.preventDefault();
    navigateWithTransition(link.href);
  });
  section.querySelectorAll('form[role="search"]').forEach(form => {
    form.addEventListener('submit', event => {
      if (event.defaultPrevented || prefersReducedMotion) return;

      const action = form.getAttribute('action') || window.location.href;
      if (!isStandardsUrl(action)) return;

      section.classList.add('is-transitioning');
    });
  });
  section.querySelector('[data-touring-collapse-nav]')?.addEventListener('click', event => {
    const collapsed = section.classList.toggle('touring-standards--collapsed');
    const button = event.currentTarget;
    button.setAttribute('aria-expanded', String(!collapsed));
    button.title = collapsed ? 'Expand navigation' : 'Collapse navigation';
    button.setAttribute('aria-label', button.title);
  });

  const dialog = section.querySelector('.touring-standards__settings');
  const menuTooltip = document.createElement('span');
  menuTooltip.className = 'touring-standards__menu-tooltip';
  menuTooltip.id = 'touring-menu-tooltip';
  menuTooltip.setAttribute('role', 'tooltip');
  menuTooltip.hidden = true;
  document.body.append(menuTooltip);
  let tooltipButton = null;
  let tooltipTimer;
  const hideMenuTooltip = () => {
    clearTimeout(tooltipTimer);
    tooltipButton?.removeAttribute('aria-describedby');
    tooltipButton = null;
    menuTooltip.hidden = true;
  };
  const showMenuTooltip = button => {
    hideMenuTooltip();
    tooltipButton = button;
    const minimise = button.hasAttribute('data-touring-collapse-nav');
    menuTooltip.textContent = minimise
      ? (button.getAttribute('aria-expanded') === 'false' ? 'Expand menu' : 'Minimise menu')
      : 'Collapse menu';
    button.setAttribute('aria-describedby', menuTooltip.id);
    menuTooltip.hidden = false;
    const rect = button.getBoundingClientRect();
    menuTooltip.style.left = `${Math.max(8, Math.min(rect.left + 8, document.documentElement.clientWidth - menuTooltip.offsetWidth - 8))}px`;
    menuTooltip.style.top = `${Math.max(8, Math.min(minimise ? rect.top - menuTooltip.offsetHeight - 12 : rect.bottom + 12, window.innerHeight - menuTooltip.offsetHeight - 8))}px`;
  };
  section.querySelectorAll('.touring-standards__nav-controls button').forEach(button => {
    button.removeAttribute('title');
    button.addEventListener('mouseenter', () => showMenuTooltip(button));
    button.addEventListener('focus', () => showMenuTooltip(button));
    button.addEventListener('mouseleave', () => { tooltipTimer = setTimeout(hideMenuTooltip, 150); });
    button.addEventListener('blur', hideMenuTooltip);
    button.addEventListener('click', () => {
      button.removeAttribute('title');
      showMenuTooltip(button);
    });
  });
  menuTooltip.addEventListener('mouseenter', () => clearTimeout(tooltipTimer));
  menuTooltip.addEventListener('mouseleave', hideMenuTooltip);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') hideMenuTooltip();
  });
  window.addEventListener('scroll', hideMenuTooltip, true);
  window.addEventListener('resize', hideMenuTooltip);
  section.querySelector('[data-touring-settings]')?.addEventListener('click', () => {
    openAccessiYesWidget(() => dialog?.showModal());
  });
  section.querySelector('[data-touring-large-text]')?.addEventListener('change', event => {
    section.classList.toggle('touring-standards--large-text', event.target.checked);
  });
  section.querySelector('[data-touring-contrast]')?.addEventListener('change', event => {
    section.classList.toggle('touring-standards--contrast', event.target.checked);
  });

}
